import { useEffect, useRef } from 'react';
import { createChart } from 'lightweight-charts';
import type { IChartApi, ISeriesApi, CandlestickData, Time } from 'lightweight-charts';

interface Props {
  data: CandlestickData<Time>[];
  height?: number;
}

export default function CandlestickChart({ data, height = 380 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const initializedRef = useRef(false);

  // Create the chart once on mount
  useEffect(() => {
    if (!containerRef.current) return;

    const bg = '#fafafa';
    const text = '#334155';
    const grid = '#e2e8f0';
    const border = '#cbd5e1';

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height,
      layout: {
        background: { type: 'solid', color: bg },
        textColor: text,
      },
      grid: {
        vertLines: { color: grid },
        horzLines: { color: grid },
      },
      crosshair: {
        mode: 1,
        vertLine: {
          color: border,
          width: 1,
          style: 2,
          labelBackgroundColor: bg,
        },
        horzLine: {
          color: border,
          width: 1,
          style: 2,
          labelBackgroundColor: bg,
        },
      },
      rightPriceScale: {
        borderColor: border,
      },
      timeScale: {
        borderColor: border,
        timeVisible: true,
        secondsVisible: true,
      },
      handleScroll: {
        vertTouchDrag: false,
      },
    });

    const series = chart.addCandlestickSeries({
      upColor: '#059669',
      downColor: '#e11d48',
      borderUpColor: '#059669',
      borderDownColor: '#e11d48',
      wickUpColor: '#059669',
      wickDownColor: '#e11d48',
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const handleResize = () => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: containerRef.current.clientWidth });
      }
    };

    const ro = new ResizeObserver(handleResize);
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      initializedRef.current = false;
    };
  }, []);

  // Update height without recreating the chart
  useEffect(() => {
    chartRef.current?.applyOptions({ height });
  }, [height]);

  // Push data updates: full refresh once, then live-update the last candle
  useEffect(() => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart || data.length === 0) return;

    if (!initializedRef.current) {
      series.setData(data);
      chart.timeScale().fitContent();
      initializedRef.current = true;
      return;
    }

    const last = data[data.length - 1];
    series.update(last);
  }, [data]);

  return <div ref={containerRef} className="w-full rounded-lg overflow-hidden" style={{ height }} />;
}